/**
 * Co-agencies, referrals and agent splits on an INVOICED sale (owner, 2026-10-09).
 *
 * Until then they locked at the first invoice. Now an agent on the sale can still
 * change them from Edit, but not through the ordinary PUT: the server refuses
 * `agents` / `co_agencies` there once an invoice is out. They go through
 * POST /sales/:id/parties/preview (HQ works out what the change does — writes
 * nothing) and then /parties/change with a reason.
 *
 * So the edit screens have to know WHICH of the two lists the agent actually
 * changed: only a changed list is sent, and a save that touched neither keeps
 * today's flow exactly. The comparison runs on the payload each list would send —
 * the same shape a Pending save posts — so "10" vs "10.00", a re-typed figure
 * that came back to where it was, or a row removed and added again in another
 * order, are not changes.
 *
 * Shared by the phone SaleCreatePage and the desktop SaleCreate so the two can
 * never disagree about what counts as a change.
 */
import { sharePayload } from './coAgencyShare';

/** Co-agency / referral rows as the sale API takes them (Pending save, preview, change). */
export const coAgencyBody = (rows = []) => rows.map((c) => ({
  co_agency_id: c.id,
  role: c.role || 'LISTER',
  co_agency_letter_id: c.co_agency_letter_id || null,
  ...sharePayload(c),
}));

/** The agent split as the sale API takes it. */
export const agentsBody = (rows = []) => rows.map((a) => ({
  agent_id: a.agent_id,
  split_percentage: Number(a.split_percentage) || 0,
}));

// Order-free, cents-exact fingerprints of the two lists.
const cents = (n) => (n == null ? null : Math.round(Number(n) * 100));
const coasKey = (rows) => JSON.stringify(coAgencyBody(rows)
  .map((r) => ({
    ...r,
    commission_percentage: cents(r.commission_percentage),
    commission_amount: cents(r.commission_amount),
    cid_share_percentage: cents(r.cid_share_percentage),
    cid_share_amount: cents(r.cid_share_amount),
  }))
  .sort((a, b) => String(a.co_agency_id).localeCompare(String(b.co_agency_id))));
const agentsKey = (rows) => JSON.stringify(agentsBody(rows)
  .map((a) => ({ agent_id: a.agent_id, split: cents(a.split_percentage) }))
  .sort((a, b) => String(a.agent_id).localeCompare(String(b.agent_id))));

/** What the edit form loaded — kept to compare the form against at save time. */
export const partiesSnapshot = (coAgencies, saleAgents) => ({
  coas: coasKey(coAgencies),
  agents: agentsKey(saleAgents),
});

/** Which of the two lists differ from the snapshot. No snapshot → nothing changed. */
export const partiesDiff = (snapshot, coAgencies, saleAgents) => {
  if (!snapshot) return { coas: false, agents: false, any: false };
  const coas = coasKey(coAgencies) !== snapshot.coas;
  const agents = agentsKey(saleAgents) !== snapshot.agents;
  return { coas, agents, any: coas || agents };
};

/** The preview / change body: only the list(s) that changed, each in full. */
export const partyChangeBody = (diff, coAgencies, saleAgents) => ({
  ...(diff.coas ? { co_agencies: coAgencyBody(coAgencies) } : {}),
  ...(diff.agents ? { agents: agentsBody(saleAgents) } : {}),
});

/** The toast once a change is saved. Hand-set lines do not move with it. */
export const partyChangeSavedText = (preview) => (preview?.hand_set
  ? 'Saved — HQ finance is told. Your commission was set by hand, so it was not recalculated.'
  : 'Saved — HQ recalculated the commission');

/** Said when the agent backs out after the figures / info fields were already saved. */
export const PARTY_CHANGE_NOT_SAVED = 'Your other changes were saved — the co-agency / split change was not.';
